import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { Menu, X, ChevronDown, LayoutDashboard, User, LogOut } from "lucide-react";

export default function Header() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  const displayName = useMemo(() => {
    if (!user) return "Guest";
    return `${user.name || ""} ${user.surname || ""}`.trim();
  }, [user]);

  const profileImageSrc = useMemo(() => {
    if (user?.role === "admin") return "https://warfc.com/api/uploads/admin.png";
    if (!user?.image_url) {
      const name = `${user?.name || ""} ${user?.surname || ""}`.trim() || "Admin";
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=06b6d4&color=fff`;
    }
    return user.image_url.startsWith("http") ? user.image_url : `https://warfc.com/api${user.image_url}`;
  }, [user]);

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login");
  }, [logout, navigate]);

  const navLinks = [
    { name: "TOURNAMENTS", path: "/tournaments" },
    { name: "EVENTS", path: "/events" },
    { name: "RANKINGS", path: "/ranking" },
    { name: "CLUBS", path: "/clubs" },
  ];

  return (
    <header className="fixed top-0 left-0 w-full z-50 transition-all duration-300 border-b border-white/10 bg-[#0f172a]/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          
          {/* LOGO SECTION */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
               <img 
                src="https://warfc.com/api/uploads/logo.png" 
                alt="Logo" 
                className="h-10 w-auto transition-transform duration-300 group-hover:scale-110" 
              />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-black italic text-lg leading-none tracking-tighter uppercase">
                fighting 
              </span>
              <span className="text-white font-black italic text-lg leading-none tracking-tighter uppercase">
                <span className="text-cyan-500">championship</span>
              </span>
            </div>
          </Link>

          {/* DESKTOP NAV */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-bold tracking-widest transition-all duration-200 hover:text-cyan-400 ${
                  location.pathname === link.path ? "text-cyan-400" : "text-gray-300"
                } relative py-2 group`}
              >
                {link.name}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-cyan-500 transform transition-transform duration-300 ${
                  location.pathname === link.path ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                }`} />
              </Link>
            ))}
          </nav>

          {/* RIGHT SIDE: AUTH / PROFILE */}
          <div className="hidden md:flex items-center gap-4">
            {!user ? (
              <div className="flex items-center gap-3">
                <Link to="/login" className="text-gray-300 hover:text-white text-sm font-bold transition">
                  LOGIN
                </Link>
                <Link to="/register" className="bg-cyan-600 hover:bg-cyan-500 text-white px-5 py-2 rounded-full text-sm font-bold transition shadow-lg shadow-cyan-900/20">
                  JOIN NOW
                </Link>
              </div>
            ) : (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-3 pl-1 pr-3 py-1 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 transition group"
                >
                  <img
                    src={profileImageSrc}
                    alt="profile"
                    className="w-8 h-8 rounded-full border border-cyan-500/50 object-cover"
                  />
                  <span className="text-sm font-bold text-gray-200 group-hover:text-white transition">
                    {displayName.split(' ')[0]}
                  </span>
                  <ChevronDown size={14} className={`text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* DROPDOWN */}
                {profileOpen && (
                  <div className="absolute right-0 mt-3 w-56 bg-[#1e293b] border border-white/10 rounded-xl shadow-2xl overflow-hidden py-1 animate-in fade-in zoom-in duration-200">
                    <div className="px-4 py-3 border-b border-white/5 bg-white/5">
                      <p className="text-xs text-gray-400">Signed in as</p>
                      <p className="text-sm font-bold text-white truncate">{displayName}</p>
                    </div>
                    
                    <Link to="/profile" className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-cyan-600 hover:text-white transition">
                      <User size={16} /> Profile Settings
                    </Link>
                    
                    {user.role === "admin" && (
                      <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-cyan-600 hover:text-white transition">
                        <LayoutDashboard size={16} /> Admin Panel
                      </Link>
                    )}
                    
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition"
                    >
                      <LogOut size={16} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* MOBILE TOGGLE */}
          <div className="md:hidden">
            <button 
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 text-gray-400 hover:text-white transition"
            >
              {menuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE MENU */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${menuOpen ? 'max-h-screen border-t border-white/10' : 'max-h-0'}`}>
        <div className="bg-[#0f172a] px-4 pt-2 pb-6 space-y-2 shadow-xl">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className="block px-4 py-3 text-base font-bold text-gray-300 hover:bg-white/5 hover:text-cyan-400 rounded-lg"
            >
              {link.name}
            </Link>
          ))}
          <div className="pt-4 mt-4 border-t border-white/5">
             {user ? (
  <>
    <Link
      to="/profile"
      className="flex items-center gap-3 px-4 py-3 text-base font-bold text-gray-300 hover:bg-white/5 hover:text-cyan-400 rounded-lg"
    >
      <User size={18} />
      PROFILE
    </Link>

    {user.role === "admin" && (
      <Link
        to="/admin/dashboard"
        className="flex items-center gap-3 px-4 py-3 text-base font-bold text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 rounded-lg"
      >
        <LayoutDashboard size={18} />
        ADMIN PANEL
      </Link>
    )}

    <button
      onClick={handleLogout}
      className="w-full py-3 text-red-400 font-bold bg-red-500/10 rounded-lg"
    >
      SIGN OUT
    </button>
  </>
) : (
  <div className="grid grid-cols-2 gap-4">
    <Link
      to="/login"
      className="text-center py-3 text-gray-300 font-bold border border-white/10 rounded-lg"
    >
      LOGIN
    </Link>

    <Link
      to="/register"
      className="text-center py-3 bg-cyan-600 text-white font-bold rounded-lg"
    >
      JOIN
    </Link>
  </div>
)}
          </div>
        </div>
      </div>
    </header>
  );
}